/**
 * AtlasWelcome — the modal shown once per browser on first authenticated entry.
 *
 * This is the existing WelcomeTour slide deck, rebranded to speak as Atlas.
 * Storage flag (`WELCOME_TOUR_COMPLETED_KEY`) is unchanged, so users who have
 * already completed the old WelcomeTour never see it again.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Brain,
  Calendar,
  ChevronRight,
  Package,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import { markWelcomeTourCompleted } from '../../shared/onboarding/onboardingState';
import { DOM_EVENT_WELCOME_TOUR_COMPLETED } from '../../shared/config/storageKeys';
import { ATLAS_BRAND, ATLAS_DESCRIPTOR } from './atlas.types';
import { emitAtlas } from './telemetry';
import '../../components/WelcomeTour/WelcomeTour.css';
import './atlas-tour.css';

type Slide = {
  title: string;
  body: string;
  Icon: typeof Brain;
};

const SLIDES: Slide[] = [
  {
    title: `Hi, I'm ${ATLAS_BRAND}`,
    body: `I'll help you finish the first key steps in Relix. This takes about 30 seconds — you'll get more value from day one.`,
    Icon: Sparkles,
  },
  {
    title: 'Analytics',
    body: 'Your hub for Jira worklog stats, charts, team throughput, and estimation accuracy. Sync data from the Database Management section.',
    Icon: BarChart3,
  },
  {
    title: 'Calendar',
    body: 'Team events and absences, with optional SharePoint sync. Pick the right product in the header to match your configured calendar.',
    Icon: Calendar,
  },
  {
    title: 'Releases',
    body: 'Plan release versions and milestone dates. Edit the grid, use Add row for Excel-like extrapolation, and sync to the main Calendar.',
    Icon: Package,
  },
  {
    title: 'Forecast and Commitment',
    body: 'Forecast remaining effort by filters. Track release commitments with snapshots and baselines over time.',
    Icon: TrendingUp,
  },
  {
    title: "You're ready",
    body: `I'll keep a small launcher in the corner so you can resume guided tours anytime. You can also replay tips from Settings.`,
    Icon: Sparkles,
  },
];

interface AtlasWelcomeProps {
  open: boolean;
  onClose?: () => void;
}

export const AtlasWelcome: React.FC<AtlasWelcomeProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [closed, setClosed] = useState(false);
  const [step, setStep] = useState(0);
  const total = SLIDES.length;
  const isLast = step === total - 1;

  useEffect(() => {
    if (open && !closed) {
      emitAtlas('atlas.welcome.shown', {});
    }
  }, [open, closed]);

  const finish = useCallback(() => {
    markWelcomeTourCompleted();
    try {
      window.dispatchEvent(new CustomEvent(DOM_EVENT_WELCOME_TOUR_COMPLETED));
    } catch {
      // ignore
    }
    setClosed(true);
    emitAtlas('atlas.welcome.dismissed', {});
    onClose?.();
  }, [onClose]);

  const handleSkip = useCallback(() => {
    finish();
  }, [finish]);

  const handlePrimary = useCallback(() => {
    if (isLast) {
      finish();
      navigate('/analytics', { replace: false });
    } else {
      setStep((s) => Math.min(s + 1, total - 1));
    }
  }, [finish, isLast, navigate, total]);

  useEffect(() => {
    if (!open || closed) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleSkip();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleSkip, open, closed]);

  if (!open || closed) return null;

  const slide = SLIDES[step];
  const { Icon } = slide;

  return (
    <div
      className="welcome-tour-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="atlas-welcome-title"
      aria-describedby="atlas-welcome-desc"
    >
      <div className="welcome-tour-modal">
        <div className="welcome-tour-slide">
          <div className="atlas-welcome__brand-kicker">
            <Sparkles size={12} aria-hidden /> {ATLAS_BRAND} — {ATLAS_DESCRIPTOR}
          </div>
          <div className="welcome-tour-icon-wrap" aria-hidden>
            <Icon size={36} strokeWidth={1.75} />
          </div>
          <h2 id="atlas-welcome-title" className="welcome-tour-title">
            {slide.title}
          </h2>
          <p id="atlas-welcome-desc" className="welcome-tour-body">
            {slide.body}
          </p>
        </div>
        <div className="welcome-tour-footer">
          <div className="welcome-tour-dots" role="tablist" aria-label="Tour progress">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                type="button"
                className="welcome-tour-dot"
                aria-label={`Step ${i + 1} of ${total}`}
                aria-current={i === step ? 'true' : undefined}
                onClick={() => setStep(i)}
              />
            ))}
          </div>
          <div className="welcome-tour-actions">
            <button type="button" className="welcome-tour-skip" onClick={handleSkip}>
              Skip tour
            </button>
            <button
              type="button"
              className="btn btn-primary welcome-tour-primary"
              onClick={handlePrimary}
            >
              {isLast ? (
                'Go to Analytics'
              ) : (
                <>
                  Next
                  <ChevronRight size={18} style={{ marginLeft: 6, verticalAlign: 'middle' }} />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
